import { debounce } from '@theme/global';

/**
 * Live filtering: any change in the facets form fetches the section with the
 * new query string, swaps the results grid and active filters, and updates the URL.
 */
class FacetFilters extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-facets-form]');
    this.sectionId = this.dataset.section;
    this.results = document.querySelector(`[data-facets-results="${this.sectionId}"]`);
    if (!this.form) return;

    this.form.addEventListener('input', debounce((event) => this.onChange(event), 350));
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.update(this.buildUrl());
    });
    this.addEventListener('click', (event) => {
      const remove = event.target.closest('.facets__remove, .facets__clear');
      if (remove) {
        event.preventDefault();
        this.update(remove.href);
      }
      const density = event.target.closest('[data-density]');
      if (density) this.setDensity(density.dataset.density);
    });
    document.addEventListener('click', (event) => {
      const link = event.target.closest(`[data-facets-results="${this.sectionId}"] .pagination a`);
      if (!link) return;
      event.preventDefault();
      this.update(link.href, true);
    });
    window.addEventListener('popstate', () => this.update(window.location.href, false, false));

    const savedDensity = this.readDensity();
    if (savedDensity) this.setDensity(savedDensity, false);
  }

  onChange(event) {
    if (event.target.name === 'sort_by' || event.target.type === 'checkbox' || event.target.type === 'number') {
      this.update(this.buildUrl());
    }
  }

  buildUrl() {
    const data = new FormData(this.form);
    const params = new URLSearchParams();
    for (const [key, value] of data.entries()) {
      if (value !== '') params.append(key, value);
    }
    return `${this.form.action.split('?')[0]}?${params.toString()}`;
  }

  async update(url, scrollToTop = false, pushState = true) {
    this.results?.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');
    try {
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', this.sectionId);
      const response = await fetch(fetchUrl.toString());
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      const freshResults = html.querySelector(`[data-facets-results="${this.sectionId}"]`);
      if (freshResults && this.results) {
        this.results.innerHTML = freshResults.innerHTML;
        this.results.dispatchEvent(new CustomEvent('fivl:content', { bubbles: true }));
      }

      const freshFacets = html.querySelector('facet-filters');
      if (freshFacets) {
        const openFacets = Array.from(this.querySelectorAll('.facet[open]')).map((d) => d.dataset.facet);
        const drawerWasOpen = this.querySelector('dialog')?.open;
        this.innerHTML = freshFacets.innerHTML;
        this.form = this.querySelector('[data-facets-form]');
        this.querySelectorAll('.facet').forEach((d) => d.toggleAttribute('open', openFacets.includes(d.dataset.facet)));
        if (drawerWasOpen) {
          const dialog = this.querySelector('theme-dialog');
          requestAnimationFrame(() => dialog?.open?.());
        }
        this.form.addEventListener('input', debounce((event) => this.onChange(event), 350));
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.update(this.buildUrl());
        });
        const density = this.readDensity();
        if (density) this.setDensity(density, false);
      }

      if (pushState) {
        const cleanUrl = new URL(url, window.location.origin);
        cleanUrl.searchParams.delete('section_id');
        window.history.pushState({}, '', cleanUrl.toString());
      }
      if (scrollToTop) this.results?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      console.error(error);
    } finally {
      this.results?.classList.remove('is-loading');
      this.removeAttribute('aria-busy');
    }
  }

  setDensity(value, save = true) {
    this.results?.style.setProperty('--grid-cols', value);
    this.querySelectorAll('[data-density]').forEach((button) => button.classList.toggle('is-active', button.dataset.density === value));
    if (save) {
      try {
        localStorage.setItem('fivl:grid-density', value);
      } catch (error) {
        /* ignore */
      }
    }
  }

  readDensity() {
    try {
      return localStorage.getItem('fivl:grid-density');
    } catch (error) {
      return null;
    }
  }
}
if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);
